import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, CircularProgress, Alert,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, PieChart, Pie,
} from 'recharts';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  BugReport as BugIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { learningAnalyticsAPI, AnomalyData } from '../../services/learningAnalyticsApi';

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#d32f2f',
  warning: '#f57c00',
};

const TYPE_COLORS: Record<string, string> = {
  enrollment_drop: '#d32f2f',
  enrollment_spike: '#1976d2',
  perfect_attendance: '#f57c00',
  very_low_attendance: '#9c27b0',
  zero_enrollment: '#757575',
};

const TYPE_LABELS: Record<string, string> = {
  enrollment_drop: 'Enrollment Drop',
  enrollment_spike: 'Enrollment Spike',
  perfect_attendance: 'Perfect Attendance',
  very_low_attendance: 'Very Low Attendance',
  zero_enrollment: 'Zero Enrollment',
};

const AnomalyDetectionTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AnomalyData | null>(null);
  const [year, setYear] = useState<string>('');
  const [district, setDistrict] = useState<string>('');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (year) params.year = parseInt(year);
      if (district) params.district = district;
      const response = await learningAnalyticsAPI.getAnomalyDetection(params);
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load anomaly data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [year, district]);

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>;
  if (!data) return null;

  const { summary, by_type, anomalies } = data;

  return (
    <Box>
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Year" value={year}
              onChange={(e) => setYear(e.target.value)}>
              <MenuItem value="">Current</MenuItem>
              {[2026, 2025, 2024, 2023].map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="District" value={district}
              onChange={(e) => setDistrict(e.target.value)} placeholder="All Districts" />
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <BugIcon color="warning" />
                <Typography variant="body2" color="textSecondary">Total Anomalies</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">{summary.total_anomalies}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ErrorIcon sx={{ color: '#d32f2f' }} />
                <Typography variant="body2" color="textSecondary">Critical</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="error.main">
                {summary.critical_count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WarningIcon sx={{ color: '#f57c00' }} />
                <Typography variant="body2" color="textSecondary">Warnings</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#f57c00' }}>
                {summary.warning_count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ShieldIcon color="info" />
                <Typography variant="body2" color="textSecondary">Data Quality Flags</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {summary.data_quality_flags}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Anomalies by Type Chart */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Anomalies by Type</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={by_type.map(t => ({ ...t, name: TYPE_LABELS[t.type] || t.type }))}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, count }: any) => `${name}: ${count}`}
                  >
                    {by_type.map((entry, index) => (
                      <Cell key={index} fill={TYPE_COLORS[entry.type] || '#757575'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Anomalies by Type Bar Chart */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Distribution by Category</Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={by_type.map(t => ({ ...t, name: TYPE_LABELS[t.type] || t.type }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" name="Count">
                    {by_type.map((entry, index) => (
                      <Cell key={index} fill={TYPE_COLORS[entry.type] || '#757575'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Anomalies Table */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Detected Anomalies</Typography>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Severity</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>School</TableCell>
                  <TableCell>District</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Z-Score</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {anomalies.map((a, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>
                      <Chip
                        label={a.severity.toUpperCase()}
                        size="small"
                        sx={{
                          bgcolor: SEVERITY_COLORS[a.severity] || '#757575',
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={TYPE_LABELS[a.type] || a.type} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">{a.school_name}</Typography>
                    </TableCell>
                    <TableCell>{a.district_name}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>{a.description}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={Math.abs(a.z_score) > 3 ? 'error' : 'textSecondary'}>
                        {a.z_score}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AnomalyDetectionTab;
